import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWindowDimensions, View } from "react-native";
import {
  Clock,
  LineChart,
  Plus,
  Table2,
  type LucideIcon,
} from "lucide-react-native";
import { CompactIconTabs, IconButton, SectionTitleRow } from "@louise/moon-ui";
import StatusBadge from "../../../components/StatusBadge";
import type { ApiPhase } from "@src/packages/patients/hooks/usePhases";
import type { TreatmentStepProgress } from "@louise/care/treatment-phases";
import { useTranslations } from "@src/packages/i18n";
import { isLiningPreparationCycleType } from "@src/packages/cycles";
import { useActiveCycleWithPhases } from "@src/packages/cycles/hooks/useCycles";
import {
  useTestResults,
  useCreateTestResult,
  useUpdateTestResult,
  useDeleteTestResult,
} from "@src/packages/test-results/hooks/useTestResults";
import { toLocalDateString } from "@src/packages/utils/date";
import { createBatchRetryLedger } from "@src/packages/care/batch-create";
import {
  createCycleMonitoringRequestId,
  useCreateCycleMonitoringEncounter,
  useCycleMonitoringDecisions,
  useCycleMonitoringEncounters,
  useUpdateCycleMonitoringEncounter,
} from "@src/packages/care/hooks/useCycleMonitoring";
import { resolveCycleMonitoringDecisionState } from "@src/packages/care/presenters/resolveCycleMonitoringDecisionState";
import { EMBER } from "../../../../onboarding/theme";
import StimResponseChart from "./StimResponseChart";
import type { MetricCycleLink } from "../../../data/metricFactsheets";
import StimSheetView from "./StimSheetView";
import {
  isMonitoringCellKey,
  MonitoringCellDetailDialog,
  type MonitoringCellKey,
} from "./MonitoringCellDetailDialog";
import {
  MonitoringEntryModal,
  type MonitoringUploadRequest,
  type MonitoringEntryDraft,
} from "../suggested/MonitoringEntryModal";
import {
  buildMonitoringDraftFromSources,
  buildPreviousMonitoringFromSources,
  findMonitoringEncounterForDate,
  persistMonitoringEncounter,
  persistMonitoringDraft,
  type MonitoringWriteReceipt,
} from "./stimMonitoringEntry";

type MonitoringView = "chart" | "table";

type Props = {
  cycleId: string | null | undefined;
  cycleStartDate: string | null | undefined;
  phases: ApiPhase[];
  derivedStepProgress: TreatmentStepProgress[];
  defaultView?: MonitoringView;
  /**
   * The cycle's type, which decides what this section is called when the host
   * does not name it. A frozen transfer is monitored, but not for stimulation.
   */
  cycleType?: string | null;
  title?: string;
  /** Override the header's top inset when the host already owns section spacing. */
  titleMarginTop?: number;
  cycleLabel?: string | null;
  onOpenCycle?: (cycleLink: MetricCycleLink) => void;
  /**
   * An arrival that already asked to add monitoring data. True opens the entry
   * modal; the request has to be cleared by the owner once it is handled, or a
   * second arrival at an already-mounted screen would find the flag still set
   * and open nothing.
   */
  openEntryRequested?: boolean;
  /**
   * Called once the requested entry modal is dismissed, so the owner can clear
   * the request that opened it and let a later arrival open the form again.
   */
  onEntryRequestHandled?: () => void;
  /** Open the shared document ingestion flow for a monitoring report. */
  onUploadReport?: MonitoringUploadRequest;
};

export default function OvarianStimulationMonitoringSection({
  cycleId,
  cycleStartDate,
  phases,
  defaultView = "chart",
  cycleType,
  title,
  titleMarginTop = 28,
  cycleLabel,
  onOpenCycle,
  openEntryRequested = false,
  onEntryRequestHandled,
  onUploadReport,
}: Props) {
  const t = useTranslations();
  const resolvedTitle =
    title ??
    (isLiningPreparationCycleType(cycleType)
      ? t("care.stim_monitoring_section.endometrial_monitoring")
      : t("care.stim_monitoring_section.stimulation_monitoring"));
  const VIEW_OPTIONS: {
    value: MonitoringView;
    label: string;
    icon: LucideIcon;
  }[] = [
    {
      value: "chart",
      label: t("care.stim_monitoring_section.view_chart"),
      icon: LineChart,
    },
    {
      value: "table",
      label: t("care.stim_monitoring_section.view_table"),
      icon: Table2,
    },
  ];
  const [view, setView] = useState<MonitoringView>(defaultView);
  const { width } = useWindowDimensions();
  const compactControls = width < 430;

  const { data: testResults } = useTestResults();
  const { data: monitoringEncounters } = useCycleMonitoringEncounters(cycleId);
  const { data: monitoringDecisions } = useCycleMonitoringDecisions(cycleId);
  const activeCycleContext = useActiveCycleWithPhases({
    cycleId: cycleId ?? null,
  });
  const activeCycle = activeCycleContext.cycle;
  const shouldIncludeUnlinkedResults =
    activeCycle?.id === cycleId && activeCycle?.status === "open";
  const createTestResult = useCreateTestResult();
  const updateTestResult = useUpdateTestResult();
  const deleteTestResult = useDeleteTestResult();
  const createMonitoringEncounter = useCreateCycleMonitoringEncounter();
  const updateMonitoringEncounter = useUpdateCycleMonitoringEncounter();
  const monitoringLedger = useRef(
    createBatchRetryLedger<MonitoringWriteReceipt>(),
  );
  const monitoringRequestIds = useRef(new Map<string, string>());
  const monitoringDecisionState = useMemo(
    () =>
      resolveCycleMonitoringDecisionState(
        monitoringDecisions,
        monitoringEncounters,
      ),
    [monitoringDecisions, monitoringEncounters],
  );

  const [entryOpen, setEntryOpen] = useState(false);
  const [entryDateISO, setEntryDateISO] = useState<string>(() =>
    toLocalDateString(new Date()),
  );
  const [focusedCell, setFocusedCell] = useState<{
    dateISO: string;
    cellKey: MonitoringCellKey;
  } | null>(null);

  // The table and the dedicated entry action both open the same canonical
  // monitoring editor, so there is one monitoring write path (issue #1949).
  const openEntry = useCallback(() => {
    setFocusedCell(null);
    setEntryDateISO(toLocalDateString(new Date()));
    setEntryOpen(true);
  }, []);

  const openEntryForCell = useCallback((dateISO: string, cellKey: string) => {
    if (!isMonitoringCellKey(cellKey)) return;
    setFocusedCell({ dateISO, cellKey });
    setEntryDateISO(dateISO);
  }, []);

  // A requested entry opens the modal, and dismissing it reports back so the
  // request can be consumed. Reacting to the request itself rather than to the
  // first mount is what makes a second press work: navigating to a cycle screen
  // that is already mounted replaces its params without remounting it, so a
  // mount-only guard would leave the request set and open nothing.
  useEffect(() => {
    if (!openEntryRequested) return;
    openEntry();
  }, [openEntryRequested, openEntry]);

  const closeEntry = useCallback(() => {
    setEntryOpen(false);
    onEntryRequestHandled?.();
  }, [onEntryRequestHandled]);

  const closeFocusedCell = useCallback(() => {
    setFocusedCell(null);
  }, []);

  const current = useMemo(
    () =>
      buildMonitoringDraftFromSources(
        monitoringEncounters,
        testResults,
        entryDateISO,
        cycleId,
        {
          cycleStartDate,
          includeUnlinkedResults: shouldIncludeUnlinkedResults,
        },
      ),
    [
      monitoringEncounters,
      testResults,
      entryDateISO,
      cycleId,
      cycleStartDate,
      shouldIncludeUnlinkedResults,
    ],
  );
  const previous = useMemo(
    () =>
      buildPreviousMonitoringFromSources(
        monitoringEncounters,
        testResults,
        entryDateISO,
        cycleId,
        {
          cycleStartDate,
          includeUnlinkedResults: shouldIncludeUnlinkedResults,
        },
      ),
    [
      monitoringEncounters,
      testResults,
      entryDateISO,
      cycleId,
      cycleStartDate,
      shouldIncludeUnlinkedResults,
    ],
  );
  const handleSave = async (
    draft: MonitoringEntryDraft,
    sourceDocumentIds: string[],
  ) => {
    if (cycleId) {
      const existing = findMonitoringEncounterForDate(
        monitoringEncounters,
        entryDateISO,
      );
      const requestKey = `${cycleId}:${entryDateISO}:${JSON.stringify(draft)}`;
      const clientRequestId =
        monitoringRequestIds.current.get(requestKey) ??
        createCycleMonitoringRequestId();
      monitoringRequestIds.current.set(requestKey, clientRequestId);
      await persistMonitoringEncounter({
        draft,
        dateISO: entryDateISO,
        existing,
        clientRequestId,
        sourceDocumentIds,
        mutations: {
          create: (payload) =>
            createMonitoringEncounter.mutateAsync({ cycleId, payload }),
          update: ({ encounterId, data }) =>
            updateMonitoringEncounter.mutateAsync({
              cycleId,
              encounterId,
              payload: data,
            }),
        },
      });
      return;
    }

    await persistMonitoringDraft({
      draft,
      dateISO: entryDateISO,
      cycleId,
      results: testResults,
      mutations: {
        create: (body) => createTestResult.mutateAsync(body),
        update: (arg) => updateTestResult.mutateAsync(arg),
        remove: (id) => deleteTestResult.mutateAsync(id),
      },
      ledger: monitoringLedger.current,
    });
  };

  const handleFocusedCellSave = async (
    patch: Partial<MonitoringEntryDraft>,
  ) => {
    await handleSave({ ...current, ...patch }, []);
  };

  const viewToggle = (
    <CompactIconTabs
      options={VIEW_OPTIONS.map((option) => ({
        ...option,
        accessibilityLabel: t("care.stim_monitoring_section.show_view_a11y", {
          params: { label: option.label.toLowerCase() },
        }),
      }))}
      value={view}
      onChange={setView}
      compact={compactControls}
    />
  );

  const headerControls = (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: compactControls ? 5 : 6,
        flexShrink: 0,
      }}
    >
      {monitoringDecisionState.reviewState === "awaiting_review" ? (
        <StatusBadge
          label={t("care.stim_monitoring_section.monitoring_recorded")}
          tone="neutral"
          icon={Clock}
        />
      ) : monitoringDecisionState.reviewState === "not_proceeding" ? (
        <StatusBadge
          label={t("care.stim_monitoring_section.not_proceeding")}
          tone="neutral"
          icon={Clock}
        />
      ) : monitoringDecisionState.reviewState === "instruction_recorded" ? (
        <StatusBadge
          label={t("care.stim_monitoring_section.decision_recorded")}
          tone="neutral"
          icon={Clock}
        />
      ) : null}
      {viewToggle}
      <IconButton
        accessibilityLabel={t(
          "care.stim_monitoring_section.add_monitoring_a11y",
        )}
        action="care.stim_monitoring_section.add_monitoring_a11y"
        onPress={openEntry}
        size={compactControls ? 32 : 36}
        surface="frosted"
      >
        <Plus
          size={compactControls ? 14 : 15}
          color={EMBER.colors.textLight}
          strokeWidth={1.8}
        />
      </IconButton>
    </View>
  );

  return (
    <View testID="monitoring-progress-section">
      <SectionTitleRow
        title={resolvedTitle}
        icon={LineChart}
        right={headerControls}
        style={{ marginTop: titleMarginTop }}
      />

      <View>
        {view === "chart" ? (
          <StimResponseChart
            cycleId={cycleId}
            cycleStartDate={cycleStartDate}
            phases={phases}
            monitoringEncounters={monitoringEncounters}
            includeUnlinkedResults={shouldIncludeUnlinkedResults}
            liningPreparation={isLiningPreparationCycleType(cycleType)}
            onAddData={openEntry}
          />
        ) : (
          <StimSheetView
            cycleId={cycleId}
            cycleStartDate={cycleStartDate}
            phases={phases}
            monitoringEncounters={monitoringEncounters}
            includeUnlinkedResults={shouldIncludeUnlinkedResults}
            onAddDay={openEntry}
            onEditCell={openEntryForCell}
            liningPreparation={isLiningPreparationCycleType(cycleType)}
          />
        )}
      </View>

      <MonitoringEntryModal
        open={entryOpen}
        dateISO={entryDateISO}
        current={current}
        previous={previous}
        onClose={closeEntry}
        onDateChange={setEntryDateISO}
        onSave={handleSave}
        onUploadReport={onUploadReport}
      />
      <MonitoringCellDetailDialog
        visible={focusedCell !== null}
        dateISO={focusedCell?.dateISO ?? entryDateISO}
        cellKey={focusedCell?.cellKey ?? null}
        current={current}
        onClose={closeFocusedCell}
        onSave={handleFocusedCellSave}
      />
    </View>
  );
}
